import * as THREE from 'three';

export type SpaceClass = 'small-room' | 'hallway' | 'large-campus';
export type NormalizedPoint = [number, number];
export type WorldPoint = [number, number, number];
export type NormalizedRect = [number, number, number, number];

export type SpatialZoneKind = 'traffic' | 'interaction' | 'idle' | 'edge';

export interface SpatialZoneAnalysis {
  id: string;
  label: string;
  kind: SpatialZoneKind;
  polygon: NormalizedPoint[];
  capacity: number;
}

export interface TrafficLaneAnalysis {
  id: string;
  label: string;
  points: NormalizedPoint[];
  width: number;
  flow: 'toward-camera' | 'away-from-camera' | 'bidirectional';
}

export interface InteractionAnchorAnalysis {
  id: string;
  label: string;
  point: NormalizedPoint;
  kind: 'doorway' | 'locker' | 'bench' | 'gathering' | 'viewpoint' | 'entry';
}

export interface DepthBehaviorAnalysis {
  direction: 'toward-vanishing-point';
  nearY: number;
  farY: number;
  scaleAtNear: number;
  scaleAtFar: number;
}

export interface CameraFrameAnalysis {
  desktop: NormalizedRect;
  mobile: NormalizedRect;
  edgeMargin: number;
}

export interface CameraCalibration {
  fov: number;
  aspect: number;
  position: WorldPoint;
  target: WorldPoint;
}

export interface ViewportSize {
  width: number;
  height: number;
}

export interface ProjectedActorBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
  centerX: number;
  centerY: number;
  heightPx: number;
  inFront: boolean;
}

export interface StandingPointAnalysis {
  id: string;
  feet: NormalizedPoint;
  confidence: number;
  scaleCorrection: number;
}

export interface BlockedRegionAnalysis {
  label: string;
  polygon: NormalizedPoint[];
}

export interface BackgroundVisionAnalysis {
  schemaVersion: 1;
  source: 'openai-vision' | 'curated-vision';
  model: string;
  spaceClass: SpaceClass;
  vanishingPoint: NormalizedPoint;
  floorPolygon: NormalizedPoint[];
  standingPoints: StandingPointAnalysis[];
  blockedRegions: BlockedRegionAnalysis[];
  occluders: BlockedRegionAnalysis[];
  depth: DepthBehaviorAnalysis;
  zones: SpatialZoneAnalysis[];
  trafficLanes: TrafficLaneAnalysis[];
  interactionAnchors: InteractionAnchorAnalysis[];
  cameraFrame: CameraFrameAnalysis;
}

export interface BackgroundLayoutDefinition {
  camera: CameraCalibration;
  analysis: BackgroundVisionAnalysis;
  shellHeight: number;
  ceiling: boolean;
  minAnchorClearance: number;
  minPairSpacing: number;
}

export interface ResolvedStandingPoint {
  id: string;
  feet: NormalizedPoint;
  confidence: number;
  worldPosition: WorldPoint;
  scaleCorrection: number;
  insideWalkableFloor: boolean;
  edgeClearance: number;
}

const GROUND_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

export function createCalibratedCamera(calibration: CameraCalibration) {
  const camera = new THREE.PerspectiveCamera(calibration.fov, calibration.aspect, 0.08, 160);
  camera.position.set(...calibration.position);
  camera.lookAt(...calibration.target);
  camera.updateMatrixWorld(true);
  camera.updateProjectionMatrix();
  return camera;
}

export function cameraForViewport(calibration: CameraCalibration, viewport?: ViewportSize) {
  return createCalibratedCamera({
    ...calibration,
    aspect: viewport ? viewport.width / viewport.height : calibration.aspect,
  });
}

export function imagePointToGround(
  point: NormalizedPoint,
  calibration: CameraCalibration,
): WorldPoint | null {
  const camera = createCalibratedCamera(calibration);
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2((point[0] / 500) - 1, 1 - (point[1] / 500));
  raycaster.setFromCamera(ndc, camera);
  const hit = raycaster.ray.intersectPlane(GROUND_PLANE, new THREE.Vector3());
  if (!hit || !Number.isFinite(hit.x) || !Number.isFinite(hit.z)) return null;
  return [hit.x, 0, hit.z];
}

export function resolveWalkableFloor(layout: BackgroundLayoutDefinition): WorldPoint[] {
  const projected = layout.analysis.floorPolygon.map((point) => {
    const hit = imagePointToGround(point, layout.camera);
    if (!hit) throw new Error(`Floor point ${point.join(',')} does not intersect the ground plane`);
    return hit;
  });

  // The camera itself is just outside the photographed plate. Extend the two
  // bottom-edge rays into a small deterministic spawn apron so the player can
  // start and turn around without inventing walkable space at the far end.
  const spawnApronZ = layout.camera.position[2] + 0.8;
  return projected.map((point, index) => (
    layout.analysis.floorPolygon[index][1] >= 950
      ? [point[0], 0, spawnApronZ]
      : point
  ));
}

export function pointInPolygonXZ(point: WorldPoint, polygon: WorldPoint[]) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const zi = polygon[i][2];
    const xj = polygon[j][0];
    const zj = polygon[j][2];
    const crosses = ((zi > point[2]) !== (zj > point[2]))
      && (point[0] < ((xj - xi) * (point[2] - zi)) / ((zj - zi) || Number.EPSILON) + xi);
    if (crosses) inside = !inside;
  }
  return inside;
}

function pointToSegmentDistanceXZ(point: WorldPoint, start: WorldPoint, end: WorldPoint) {
  const dx = end[0] - start[0];
  const dz = end[2] - start[2];
  const lengthSq = (dx * dx) + (dz * dz);
  const t = lengthSq === 0
    ? 0
    : THREE.MathUtils.clamp(
      (((point[0] - start[0]) * dx) + ((point[2] - start[2]) * dz)) / lengthSq,
      0,
      1,
    );
  return Math.hypot(point[0] - (start[0] + t * dx), point[2] - (start[2] + t * dz));
}

export function distanceToPolygonEdgeXZ(point: WorldPoint, polygon: WorldPoint[]) {
  let closest = Number.POSITIVE_INFINITY;
  for (let index = 0; index < polygon.length; index += 1) {
    closest = Math.min(
      closest,
      pointToSegmentDistanceXZ(point, polygon[index], polygon[(index + 1) % polygon.length]),
    );
  }
  return closest;
}

export function resolveStandingPoints(layout: BackgroundLayoutDefinition): ResolvedStandingPoint[] {
  const floor = resolveWalkableFloor(layout);
  return layout.analysis.standingPoints.map((anchor) => {
    const worldPosition = imagePointToGround(anchor.feet, layout.camera);
    if (!worldPosition) throw new Error(`NPC anchor ${anchor.id} does not intersect the ground plane`);
    return {
      ...anchor,
      worldPosition,
      insideWalkableFloor: pointInPolygonXZ(worldPosition, floor),
      edgeClearance: distanceToPolygonEdgeXZ(worldPosition, floor),
    };
  });
}

export function projectedHeightPixels(
  position: WorldPoint,
  worldHeight: number,
  calibration: CameraCalibration,
  viewportHeight = 720,
) {
  const camera = createCalibratedCamera(calibration);
  const feet = new THREE.Vector3(...position).project(camera);
  const head = new THREE.Vector3(position[0], position[1] + worldHeight, position[2]).project(camera);
  return Math.abs(head.y - feet.y) * viewportHeight * 0.5;
}

export function projectedActorBounds(
  position: WorldPoint,
  worldHeight: number,
  worldWidth: number,
  calibration: CameraCalibration,
  viewport: ViewportSize,
): ProjectedActorBounds {
  const camera = cameraForViewport(calibration, viewport);
  const center = new THREE.Vector3(...position);
  const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize();
  const halfWidth = right.multiplyScalar(worldWidth / 2);
  const corners = [
    center.clone().add(halfWidth),
    center.clone().sub(halfWidth),
    center.clone().add(halfWidth).setY(position[1] + worldHeight),
    center.clone().sub(halfWidth).setY(position[1] + worldHeight),
  ].map((point) => point.project(camera));
  const pixels = corners.map((point) => ({
    x: (point.x + 1) * viewport.width * 0.5,
    y: (1 - point.y) * viewport.height * 0.5,
    z: point.z,
  }));
  const xs = pixels.map((point) => point.x);
  const ys = pixels.map((point) => point.y);
  const centerPoint = center.clone().project(camera);
  return {
    left: Math.min(...xs),
    top: Math.min(...ys),
    right: Math.max(...xs),
    bottom: Math.max(...ys),
    centerX: (centerPoint.x + 1) * viewport.width * 0.5,
    centerY: (1 - centerPoint.y) * viewport.height * 0.5,
    heightPx: Math.max(...ys) - Math.min(...ys),
    inFront: centerPoint.z >= -1 && centerPoint.z <= 1,
  };
}

export function screenSpaceSeparationPx(first: ProjectedActorBounds, second: ProjectedActorBounds) {
  const horizontalGap = Math.max(first.left - second.right, second.left - first.right, 0);
  const verticalGap = Math.max(first.top - second.bottom, second.top - first.bottom, 0);
  const overlaps = horizontalGap === 0 && verticalGap === 0;
  if (overlaps) {
    return -Math.min(
      Math.min(first.right, second.right) - Math.max(first.left, second.left),
      Math.min(first.bottom, second.bottom) - Math.max(first.top, second.top),
    );
  }
  return Math.max(horizontalGap, verticalGap);
}

export function projectedHorizonPoint(calibration: CameraCalibration): NormalizedPoint {
  const camera = createCalibratedCamera(calibration);
  const horizon = new THREE.Vector3(
    calibration.position[0],
    calibration.position[1],
    calibration.position[2] - 10_000,
  ).project(camera);
  return [(horizon.x + 1) * 500, (1 - horizon.y) * 500];
}

export function floorExtents(polygon: WorldPoint[]) {
  const xs = polygon.map((point) => point[0]);
  const zs = polygon.map((point) => point[2]);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minZ: Math.min(...zs),
    maxZ: Math.max(...zs),
    width: Math.max(...xs) - Math.min(...xs),
    depth: Math.max(...zs) - Math.min(...zs),
  };
}

export function validateBackgroundLayout(layout: BackgroundLayoutDefinition) {
  const errors: string[] = [];
  if (layout.analysis.floorPolygon.length < 4) errors.push('floor polygon needs at least four points');
  if (layout.analysis.standingPoints.length < 4) errors.push('at least four standing points are required');

  let placements: ResolvedStandingPoint[] = [];
  try {
    placements = resolveStandingPoints(layout);
  } catch (error) {
    errors.push(error instanceof Error ? error.message : String(error));
  }

  placements.forEach((placement) => {
    if (!placement.insideWalkableFloor) errors.push(`${placement.id} is outside the walkable floor`);
    if (placement.edgeClearance < layout.minAnchorClearance) {
      errors.push(`${placement.id} is only ${placement.edgeClearance.toFixed(2)}m from a floor boundary`);
    }
    if (placement.confidence < 0.65) errors.push(`${placement.id} confidence is below 0.65`);
    if (placement.scaleCorrection < 0.85 || placement.scaleCorrection > 1.15) {
      errors.push(`${placement.id} scale correction is outside the safe range`);
    }
  });

  for (let first = 0; first < placements.length; first += 1) {
    for (let second = first + 1; second < placements.length; second += 1) {
      const a = placements[first].worldPosition;
      const b = placements[second].worldPosition;
      const spacing = Math.hypot(a[0] - b[0], a[2] - b[2]);
      if (spacing < layout.minPairSpacing) {
        errors.push(`${placements[first].id} and ${placements[second].id} are only ${spacing.toFixed(2)}m apart`);
      }
    }
  }

  return { valid: errors.length === 0, errors, placements };
}
